import { Integration, Interface } from '@botpress/client'
import { transforms, ZodType } from '@botpress/sdk'
import crypto from 'crypto'
import path from 'path'

import { camelCase, pascalCase, snakeCase } from '../utils/strings.js'
import { ADK_VERSION, formatCode, relative, toMultilineComment } from './utils.js'

export const sameMajorVersion = (a: string, b: string): boolean => {
  const majorA = a.split('.')[0]
  const majorB = b.split('.')[0]
  return majorA === majorB
}

const getIntegrationInterface = (
  integration: Integration,
  int: Interface
): Integration['interfaces']['string'] | undefined => {
  return Object.values(integration.interfaces || {}).find(
    (x) => x.name === int.name && sameMajorVersion(x.version, int.version)
  )
}

const getInterfaceHash = (int: Interface, integrations: Integration[]): string => {
  const versions = integrations
    .filter((x) => !!getIntegrationInterface(x, int))
    .map((x) => `${x.id}|${x.version}|${x.updatedAt}`)
    .sort()
    .join('|')

  return crypto.createHash('sha256').update(`${int.id}|${int.version}|${int.updatedAt}|${versions}`).digest('hex')
}

const getInterfaceNames = (int: Interface) => ({
  name: camelCase(int.name),
  typings: {
    index: `Interface_All_${pascalCase(int.name)}`,
    actions: `Interface_Actions_${pascalCase(int.name)}`,
  },
  paths: {
    index: path.join(snakeCase(int.name), `index.ts`),
    actions: path.join(snakeCase(int.name), `actions.ts`),
  },
})

export async function generateInterfaceTypes(
  int: Interface,
  integrations: Integration[]
): Promise<{
  names: ReturnType<typeof getInterfaceNames>
  files: Record<string, string>
}> {
  const names = getInterfaceNames(int)
  const hash = getInterfaceHash(int, integrations)

  const implementors = integrations
    .filter((x) => !!getIntegrationInterface(x, int))
    .sort((a, b) => a.name.localeCompare(b.name))

  const rel = (to: string): string => {
    const from = names.paths.index
    return relative(from, to).replace(/\.ts$/i, '')
  }

  let tIndex = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Interface: ${int.name}
  // Integrations: ${implementors.map((x) => x.name).join(', ')}
  // Version: ${int.version}
  // File: ${names.paths.index}
  // Hash: ${hash}
  // Updated at: ${int.updatedAt}
  ////////////////////////////////////////////////////////

  import { ${names.typings.actions} } from "${rel(names.paths.actions)}";

  export * from "${rel(names.paths.actions)}";

  export type ${names.typings.index} = {
    actions: ${names.typings.actions};
  };

  export const ${names.typings.index} = {
    actions: ${names.typings.actions},
  }
  `

  let iActions = ``
  let cActions = new Set<string>()

  for (const integration of implementors) {
    iActions += `
    // Integration: ${integration.name} (${integration.version})
    // Updated at: ${integration.updatedAt}
    "${integration.name}": {`

    for (const [name, action] of Object.entries(int.actions)) {
      const integrationInt = getIntegrationInterface(integration, int)
      const mapping = integrationInt?.actions[name]?.name

      if (!mapping) {
        continue
      }

      const entities = Object.entries(integrationInt.entities).reduce(
        (acc, [entityName, value]) => {
          const entity = integration.entities[value.name]

          if (entity) {
            acc[entityName] = transforms
              .fromJSONSchema(entity.schema)
              .title(entity.title || entityName)
              .describe(entity.description || 'No description')
          }
          return acc
        },
        {} as Record<string, ZodType>
      )

      const input = transforms.fromJSONSchema(action.input.schema).dereference(entities).toTypescriptType()

      const output = transforms.fromJSONSchema(action.output.schema).dereference(entities).toTypescriptType()

      cActions.add(`"${integration.name}:${name}": "${integration.name}:${mapping}"`)

      iActions += `
      ${toMultilineComment(`Title: ${action.title || name}\nDescription: ${action.description || 'No description'}`)}
      "${name}": {
        input: ${input};
        output: ${output};
      },\n`
    }

    iActions += `};`
  }

  const tActions = `
  ////////////////////////////////////////////////////////
  // DO NOT EDIT THIS FILE DIRECTLY
  // This file is auto-generated from the Botpress ADK
  // ADK Version: ${ADK_VERSION}
  // Interface: ${int.name}
  // Integrations: ${implementors.map((x) => x.name).join(', ')}
  // Version: ${int.version}
  // File: ${names.paths.index}
  // Hash: ${hash}
  // Updated at: ${int.updatedAt}
  ////////////////////////////////////////////////////////

  export type ${names.typings.actions} = {
    ${iActions}
  };

  export const ${names.typings.actions} = {
    ${Array.from(cActions).join(',\n    ')}
  };
  
  `

  return {
    names,
    files: {
      [names.paths.index]: await formatCode(tIndex),
      [names.paths.actions]: await formatCode(tActions),
    },
  }
}
